import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-D4 — slow-truck oversight. Per dumper truck, daily Afaqy km over a window.
// 0-km days are highlighted; a truck with no movement in the last 2 days is
// flagged idle. Simple and visible, not over-engineered.

interface DayKm { date: string; km: number }
interface Row {
  vehicleId: string;
  plateEn: string;
  vehicleCode: string;
  totalKm: number;
  activeDays: number;
  lastActive: string | null;
  idle: boolean;
  days: DayKm[];
}

export default function DumperTruckActivityPage() {
  const days = 14;
  const { data, isLoading } = useQuery({
    queryKey: ['dumper-truck-activity', days],
    queryFn: async () => (await apiClient.get<{ days: number; items: Row[] }>('/api/v1/dumpers/truck-activity', { params: { days } })).data.items,
  });
  const rows = data ?? [];
  const dayHeaders = rows[0]?.days ?? [];

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Dumpers · Truck activity</div>
          <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard'; }} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Dumpers home
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-8">
        <div className="text-sm text-muted-foreground mb-3">Daily km per truck (last {days} days, from Afaqy). 0-km days are shaded; no movement in 2 days is flagged.</div>
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-start font-medium px-3 py-2 sticky start-0 bg-muted/40">Truck</th>
                <th className="text-end font-medium px-2 py-2">Total</th>
                {dayHeaders.map((d) => (
                  <th key={d.date} className="text-end font-medium px-1 py-2 whitespace-nowrap">{d.date.slice(5)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={dayHeaders.length + 2} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">No dumper trucks yet (import them and assign to the Dumpers project).</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.vehicleId} className="border-t border-border">
                  <td className="px-3 py-2 sticky start-0 bg-card">
                    <div className="font-medium flex items-center gap-1">
                      {r.plateEn}
                      {r.idle && <span title="No movement in 2+ days" className="inline-flex items-center text-rose-500"><AlertTriangle className="h-3.5 w-3.5" /></span>}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-end font-medium">{r.totalKm.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  {r.days.map((d) => (
                    <td key={d.date} className={`px-1 py-2 text-end ${d.km <= 1 ? 'bg-rose-500/10 text-rose-600' : ''}`}>
                      {d.km > 0 ? Math.round(d.km) : '·'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
